import React from 'react';
import { Capacitor, SystemBars, SystemBarsStyle, SystemBarType } from '@capacitor/core';
import {
  applyTheme,
  DEFAULT_THEME_ID,
  getThemeList,
  getTheme,
  isValidThemeId,
  readStoredThemeId,
  storeThemeId,
  ThemeDefinition,
  ThemeId,
} from './themes';
import { logger } from '../utils/logger';

interface ThemeContextValue {
  themeId: ThemeId;
  setTheme: (id: ThemeId) => void;
  themes: ThemeDefinition[];
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

const sanitizeThemeId = (id?: string | null): ThemeId => {
  if (id && isValidThemeId(id)) {
    return id;
  }
  return DEFAULT_THEME_ID;
};

interface ThemeProviderProps {
  children: React.ReactNode;
  initialThemeId?: ThemeId;
}

export function ThemeProvider({ children, initialThemeId }: ThemeProviderProps) {
  const storedThemeId = React.useMemo(() => sanitizeThemeId(initialThemeId ?? readStoredThemeId()), [initialThemeId]);
  const [themeId, setThemeId] = React.useState<ThemeId>(storedThemeId);
  const themeList = React.useMemo(() => getThemeList(), []);

  React.useEffect(() => {
    applyTheme(themeId);
    storeThemeId(themeId);
    if (!Capacitor.isNativePlatform?.()) return;
    try {
      const theme = getTheme(themeId);
      const platform = Capacitor.getPlatform();
      const prefersDarkBars = theme.colorScheme === 'dark';
      const style = prefersDarkBars ? SystemBarsStyle.Dark : SystemBarsStyle.Light;

      if (platform === 'ios') {
        void Promise.all([
          SystemBars.show({ bar: SystemBarType.StatusBar }),
          SystemBars.setStyle({ style, bar: SystemBarType.StatusBar }),
        ]);
        return;
      }

      void Promise.all([
        SystemBars.show({ bar: SystemBarType.StatusBar }),
        SystemBars.setStyle({ style, bar: SystemBarType.StatusBar }),
        SystemBars.setStyle({ style, bar: SystemBarType.NavigationBar }),
      ]);
    } catch (err) {
      logger.warn('STATUS_BAR_THEME_SYNC_FAILED', err);
    }
  }, [themeId]);

  const setTheme = React.useCallback<ThemeContextValue['setTheme']>((id) => {
    setThemeId((prev) => {
      if (!isValidThemeId(id)) {
        return prev;
      }
      if (prev === id) {
        return prev;
      }
      return id;
    });
  }, []);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ themeId, setTheme, themes: themeList }),
    [themeId, setTheme, themeList]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
